import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApiError } from '@core/interfaces/api-error.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, MonoTypeOperatorFunction, Observable, of, pipe, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { getBriefAnalyzeErrorMessage } from '../brief/brief-analyze-error.util';
import { fitExecutionDurationsToGamePlays } from '../brief/brief-mapping.util';
import { IBriefAnalysis, IBriefAnalyzeRequest } from '../brief/brief.interface';
import { BriefService } from '../brief/brief.service';
import {
  TCampaignApi,
  TCampaignCopyPayload,
  TCampaignCreatePayload,
  TCampaignUpdatePayload,
} from '../campaigns.interface';
import { CampaignsService } from '../campaigns.service';
import { CreateCampaignDialogCopyPayloadService } from './create-campaign-dialog-copy-payload.service';

const FORBIDDEN_DELIVERABLE_ERROR_CODE = 'forbidden_deliverable';
const INVALID_DURATION_ERROR_CODE = 'invalid_duration';

interface ICreateCampaignDialogState {
  briefAnalysis: IBriefAnalysis | null;
  briefAnalyzeLoading: boolean;
  campaign: TCampaignApi | null;
  campaignSubmitLoading: boolean;
  copySourceCampaign: TCampaignApi | null;
  copySourceCampaignLoading: boolean;
  error: string | null;
}

const initialState: ICreateCampaignDialogState = {
  briefAnalysis: null,
  briefAnalyzeLoading: false,
  campaign: null,
  campaignSubmitLoading: false,
  copySourceCampaign: null,
  copySourceCampaignLoading: false,
  error: null,
};

@Injectable()
export class CreateCampaignDialogStore extends ComponentStore<ICreateCampaignDialogState> {
  public readonly campaign$ = this.selectSignal((state: ICreateCampaignDialogState) => state.campaign);
  public readonly copySourceCampaign$ = this.selectSignal(
    (state: ICreateCampaignDialogState) => state.copySourceCampaign,
  );
  public readonly copySourceCampaignLoading$ = this.selectSignal(
    (state: ICreateCampaignDialogState) => state.copySourceCampaignLoading,
  );
  public readonly campaignSubmitLoading$ = this.selectSignal(
    (state: ICreateCampaignDialogState) => state.campaignSubmitLoading,
  );
  public readonly error$ = this.selectSignal((state: ICreateCampaignDialogState) => state.error);
  public readonly briefAnalysis$ = this.selectSignal((state: ICreateCampaignDialogState) => state.briefAnalysis);
  public readonly briefAnalyzeLoading$ = this.selectSignal(
    (state: ICreateCampaignDialogState) => state.briefAnalyzeLoading,
  );

  // Shared by createCampaign/updateCampaign/createCampaignFromBrief - none of them have a
  // template that reads error$, so without this the submit button just stops loading with no
  // explanation on an HTTP-level failure. A single generic message covers all three (create,
  // edit, and brief-create all fail for the same reasons - network/5xx/validation the client
  // already passed) rather than needing a per-caller copy.
  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          campaignSubmitLoading: false,
          error: errorMessage,
        });
        this.snackbarService.openError(this.translate.instant('dialogs.createCampaignDialog.submitError'));

        return EMPTY;
      }),
    );

  private readonly handleCopyCampaignError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const errorDetails = this.errorFormatterService.getErrorDetails(error.error as IApiError);
        const isForbiddenDeliverable = errorDetails.some((detail) => detail.code === FORBIDDEN_DELIVERABLE_ERROR_CODE);

        if (isForbiddenDeliverable) {
          this.snackbarService.openError(this.translate.instant('dialogs.createCampaignDialog.forbiddenDeliverable'));
        }

        this.patchState({
          campaignSubmitLoading: false,
          error: errorMessage,
        });

        return EMPTY;
      }),
    );

  public readonly createCampaign = this.effect((trigger$: Observable<TCampaignCreatePayload>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
      switchMap((campaign: TCampaignCreatePayload) =>
        this.campaignsService.createCampaign(campaign).pipe(
          tap((campaignResponse: TCampaignApi) => {
            this.patchState({
              campaign: campaignResponse,
              campaignSubmitLoading: false,
            });
          }),
          this.handleError('Failed to submit campaign'),
        ),
      ),
    );
  });

  // The brief draft is shaped like a full campaign (ctaTemplates, campaignCreators, ...), same as
  // a copied campaign - reuses the exact same sanitize step (strip stray ids, drop export fields,
  // force draft status) rather than duplicating it.
  public readonly createCampaignFromBrief = this.effect((trigger$: Observable<Record<string, unknown>>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
      switchMap((draft: Record<string, unknown>) => {
        const payload = this.copyPayloadService.sanitize(draft as unknown as TCampaignApi);

        return this.campaignsService.createCampaign(payload).pipe(
          tap((campaignResponse: TCampaignApi) => {
            this.patchState({
              campaign: campaignResponse,
              campaignSubmitLoading: false,
            });
          }),
          this.handleError('Failed to submit campaign'),
        );
      }),
    );
  });

  // /brief/analyze can take up to ~120s (a model call) - cancelBriefAnalyze lets the full-screen
  // "analyzing" overlay's close button actually abort the in-flight request via takeUntil, not
  // just hide the loading state.
  private readonly cancelBriefAnalyze$ = new Subject<void>();

  public readonly analyzeBrief = this.effect((trigger$: Observable<IBriefAnalyzeRequest>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ briefAnalysis: null, briefAnalyzeLoading: true })),
      switchMap((request: IBriefAnalyzeRequest) =>
        this.briefService.analyzeBrief(request).pipe(
          switchMap((analysis: IBriefAnalysis) => this.withExecutionDurationsFitted(analysis)),
          takeUntil(this.cancelBriefAnalyze$),
          tap((analysis: IBriefAnalysis) => {
            this.patchState({ briefAnalysis: analysis, briefAnalyzeLoading: false });
          }),
          // An HTTP-level failure here (a refused file, budget, network/5xx/timeout) is not a
          // campaign-data problem the wizard could show a fix step for - unlike errors[]/warnings[]
          // on a successful 200, so this is the only place that failure can surface at all. The
          // interceptor skips its own snackbar for this URL so only the translated one shows.
          catchError((error: HttpErrorResponse) => {
            const fileNames = request.files.map((file) => file.name);
            const { params, translationKey } = getBriefAnalyzeErrorMessage(error, fileNames);

            this.patchState({ briefAnalyzeLoading: false });
            this.snackbarService.openError(this.translate.instant(translationKey, params));

            return EMPTY;
          }),
        ),
      ),
    );
  });

  // invalid_duration is fixed where the campaign end allows (see fitExecutionDurationsToGamePlays), then the draft
  // is checked again under the same loading overlay - that check also reports the errors the
  // backend stopped before. downgrade stays off, as on /brief/analyze itself.
  private withExecutionDurationsFitted(analysis: IBriefAnalysis): Observable<IBriefAnalysis> {
    if (!analysis.errors.some((error) => error.code === INVALID_DURATION_ERROR_CODE)) {
      return of(analysis);
    }

    return this.briefService.validateBrief(fitExecutionDurationsToGamePlays(analysis.campaign), false);
  }

  public cancelBriefAnalyze(): void {
    this.cancelBriefAnalyze$.next();
    this.patchState({ briefAnalyzeLoading: false });
  }

  public clearBriefAnalysis(): void {
    this.patchState({ briefAnalysis: null });
  }

  public readonly copyCampaign = this.effect(
    (trigger$: Observable<{ iconId: string | null; name: string; sourceCampaign: TCampaignApi }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
        switchMap(({ iconId, name, sourceCampaign }) => {
          const copyCampaignPayload: TCampaignCopyPayload = this.prepareCopyCampaignPayload(
            iconId,
            name,
            sourceCampaign,
          );

          return this.campaignsService.createCampaign(copyCampaignPayload).pipe(
            tap((campaignResponse: TCampaignApi) => {
              this.patchState({
                campaign: campaignResponse,
                campaignSubmitLoading: false,
              });
            }),
            this.handleCopyCampaignError('Failed to copy campaign'),
          );
        }),
      );
    },
  );

  public readonly fetchSourceCampaignWithAll = this.effect(
    (trigger$: Observable<{ adminMode: boolean; campaignId: string }>) => {
      return trigger$.pipe(
        tap(() =>
          this.patchState({
            copySourceCampaign: null,
            copySourceCampaignLoading: true,
            error: null,
          }),
        ),
        switchMap(({ adminMode, campaignId }) => {
          const params: HttpParams = new HttpParams().set('joins', 'all');
          const request$ = adminMode
            ? this.campaignsService.getAdminCampaign(campaignId, params)
            : this.campaignsService.getCampaign(campaignId, params);

          return request$.pipe(
            tap((campaign: TCampaignApi | undefined) => {
              this.patchState({
                copySourceCampaign: campaign ?? null,
                copySourceCampaignLoading: false,
              });
            }),
            catchError(() => {
              this.patchState({
                copySourceCampaign: null,
                copySourceCampaignLoading: false,
                error: 'Failed to load campaign',
              });

              return EMPTY;
            }),
          );
        }),
      );
    },
  );

  public readonly updateCampaign = this.effect((trigger$: Observable<TCampaignUpdatePayload>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
      switchMap((campaign: TCampaignUpdatePayload) =>
        this.campaignsService.updateCampaign(campaign.id, campaign).pipe(
          tap((campaignResponse: TCampaignApi) => {
            this.patchState({
              campaign: campaignResponse,
              campaignSubmitLoading: false,
            });
          }),
          this.handleError('Failed to submit campaign'),
        ),
      ),
    );
  });

  private prepareCopyCampaignPayload(
    iconId: string | null,
    name: string,
    sourceCampaign: TCampaignApi,
  ): TCampaignCopyPayload {
    const copyCampaignPayload = this.copyPayloadService.sanitize(sourceCampaign);

    return {
      ...copyCampaignPayload,
      iconId,
      name,
    };
  }

  private readonly briefService = inject(BriefService);

  constructor(
    private readonly campaignsService: CampaignsService,
    private readonly copyPayloadService: CreateCampaignDialogCopyPayloadService,
    private readonly errorFormatterService: ErrorFormatterService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
  ) {
    super(initialState);
  }
}
